// Dr.Rx™ Clinical Decision Support Engine & Gemini AI Service
import type { 
    DrugInfo, MessageSource, AnswerLength, Alert, FdaRecall, CdcAdvisory, DeaUpdate, 
    EvidenceSearchResult, ProfessionalFocus, MMEResult, DoseAdjustmentResult, 
    IVCompatibilityResult, DrugInteractionResult, CounselingPointsResult, 
    PillIdentifierResult, AnticoagBridgingResult, TherapeuticInterchangeResult, 
    DaysSupplyResult, CompoundingCalcResult, LawResult, AntibioticInfoResult, 
    GuidelineSummaryResult, DrugTaperingResult, PediatricDoseResult, 
    PharmacokineticsResult, AnticholinergicBurdenResult, InsulinConversionResult, 
    MissedDoseResult, VaccineRecResult, BeersCriteriaResult, AntidoteResult, 
    DrugShortageResult, LabValueResult, DoseConversionResult, OTCResult, 
    CSReferenceResult, DrugPriceResult, PAJustificationResult, SigCodeResult, 
    EmergencyGuideResult, AnalyzeRxResult, ClinicalTrialResult, DietaryGuideResult, 
    FormularyResult, HivInfoResult, InrResult, PgxResult, BiostatsResult, 
    GenericCalcResult, CannabinoidInfoResult, ChemoToxicityResult, 
    CompoundingFormulaResult, MnemonicResult, VitaminResult, PregnancyLactationResult,
    MTMResult, Message, LlmModel
} from '../types';
import { allToolsMap } from '../data/toolsData';
import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';

// Environment-aware API URL.
// On Firebase Hosting domains, /api/chat rewrites to streamChat. On preview/dev domains (*.run.app, localhost), call the Cloud Function URL directly.
const isFirebaseHosted = typeof window !== 'undefined' && (
    window.location.hostname === 'drrx.app' ||
    window.location.hostname === 'www.drrx.app' ||
    window.location.hostname.endsWith('.web.app') ||
    window.location.hostname.endsWith('.firebaseapp.com')
);
const API_URL = isFirebaseHosted
    ? '/api/chat'
    : 'https://us-central1-drrx117.cloudfunctions.net/streamChat';

// Generate a string listing all available tools for the system prompt.
const toolsListString = Array.from(allToolsMap.values())
  .map(tool => `- **${tool.name}** (Keywords: ${tool.tags.join(', ')})`)
  .join('\n');

const FOCUS_CALIBRATION: Record<ProfessionalFocus, string> = {
  Pharmacist: 'Deep dive into formulation nuances, renal/hepatic dose titration, CYP/P-gp enzyme pathways, excipients, reconstitution, stability, and therapeutic drug monitoring (TDM).',
  Physician: 'Prioritize guideline-directed medical therapy (GDMT), clinical trial endpoints, comparative efficacy, diagnostic criteria, and second-line disease management strategies.',
  Nurse: 'Emphasize practical IV administration rates, Y-site/line management, extravasation protocols, acute bedside monitoring parameters, and black box warning vigilance.',
  Researcher: 'Highlight pharmacokinetic/pharmacodynamic (PK/PD) parameters, statistical significance, study methodology, mechanism of action, and receptor affinities.',
};

const getSystemInstruction = (answerLength: AnswerLength, professionalFocus: ProfessionalFocus, withAddDrugInstruction: boolean = false): string => {
  const focusGuide = FOCUS_CALIBRATION[professionalFocus] || FOCUS_CALIBRATION.Pharmacist;

  let finalInstruction = `You are Dr.Rx™, an elite clinical decision support (CDS) AI Clinical Pharmacist engineered by NV Pharmaceutics LLC for licensed healthcare professionals (pharmacists, physicians, nurses, clinical researchers, and pharmacy students).
Your responses must be evidence-based, pharmacologically rigorous, actionable, and strictly adhere to current FDA, CDC, USP, and clinical practice guidelines (e.g., ACC/AHA, IDSA, ADA, CHEST, KDIGO, Beers Criteria).

**GLOBAL MANDATES & GUARDRAILS:**
- **Zero-Fluff Policy:** Jump immediately to the clinical data in your very first sentence. Never include conversational filler, greetings, pleasantries, or wrapping remarks (do not write "Hello", "Certainly", "Here is the information", or "In conclusion").
- **Strict Scope of Practice:** Restrict all outputs exclusively to Pharmacy, Medicine, Pharmacology, Pharmacokinetics, Pharmacogenomics, Clinical Guidelines, Pharmacy Law (Federal/State/MPJE), and Clinical Biostatistics/Calculations. Firmly refuse any non-medical, non-clinical queries in a single neutral sentence.
- **HIPAA & De-Identification Guardrail:** Never request, record, or process Protected Health Information (PHI) such as patient names, initials, dates of birth, social security numbers, or medical record numbers (MRNs). Treat all patient scenarios as purely de-identified clinical vignettes.
- **Hallucination Prevention:** Never invent or fabricate clinical trial identifiers (NCT numbers), dosing guidelines, off-label indications, or FDA recall classes. State clinical verification requirements and uncertainties explicitly when data is variable or inconclusive.

**DYNAMIC USER CALIBRATION (${professionalFocus.toUpperCase()} FOCUS):**
- ${focusGuide}
- Patient-Facing Exception: When explicitly asked for patient counseling points or medication action plans, use clear, empathetic 6th-to-8th-grade health literacy language while avoiding medical jargon.

**CHAT RESPONSE FORMATTING RULES:**
- Use exactly three hashes (\`### \`) for every section header. Do not use \`#\`, \`##\`, or \`####\`.
- Use standard hyphenated bullet points (\`- \`) for lists to maintain UI scannability.
- Use exact warning badge trigger keywords in uppercase or bold text when clinically warranted: \`Black Box Warning:\`, \`Contraindications:\`, \`Severe Interaction:\`, \`Warnings:\`.
`;

  if (answerLength === 'short') {
    finalInstruction += `
**MODE LAYOUT: SHORT MODE (Strictly under 150 words)**
### Clinical Answer
[Direct, immediate clinical facts, dosing, or adjustment rules]

### Key Pearls
- [High-yield clinical considerations or monitoring pearls]
- [Notable contraindications or kinetic nuances]

### Warnings
[Only include this section if an active Black Box Warning or Absolute Contraindication exists]

### References
1. [Primary source / Package insert / Guideline]
`;
  } else {
    finalInstruction += `
**MODE LAYOUT: DETAILED MODE (Comprehensive Monograph)**
Structure systematically using these exact headings:
- ### Mechanism
- ### Dosing
- ### Safety
- ### Monitoring
- ### Interactions
- ### References
`;
  }

  finalInstruction += `
**MANDATORY UI CONTROL TAGS (Append at the absolute end of every chat response on their own lines):**
1. **Tool Recommendation Tag:** If the clinical discussion aligns with one or more built-in application tools below, append their exact registered names inside brackets (comma-separated if multiple):
\`DRR_RECOMMEND_TOOL:[Tool Name 1, Tool Name 2]\`
Authorized Tool Registry:
${toolsListString}
`;

  if (withAddDrugInstruction) {
    finalInstruction += `
2. **Drug Index Tag:** If the prompt discusses a specific medication and does NOT include "[System Note: User has viewed this drug locally]", append:
\`DRR_ADD_DRUG:[Exact Drug Name]\`
`;
  }

  finalInstruction += `
3. **Follow-Up Prompts Tag (MANDATORY on every response):** Must be a valid single-line JSON array containing 2 to 3 concise, highly relevant clinical follow-up questions:
\`DRR_FOLLOW_UP:["Follow-up question 1?", "Follow-up question 2?", "Follow-up question 3?"]\`
`;

  return finalInstruction;
};

// --- CHAT STREAM PROXY ---

/**
 * Parses Server-Sent Events (SSE) from the backend.
 */
async function* parseSSEStream(reader: ReadableStreamDefaultReader<Uint8Array>) {
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
            if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                if (jsonStr === '[DONE]') return;
                try {
                    const data = JSON.parse(jsonStr);
                    if (data.candidates && data.candidates[0].content && data.candidates[0].content.parts) {
                        const text = data.candidates[0].content.parts[0].text;
                        const groundingMetadata = data.candidates[0].groundingMetadata;
                        
                        yield {
                            text,
                            candidates: [{
                                groundingMetadata
                            }]
                        };
                    }
                } catch (e) {
                    console.warn("Failed to parse SSE JSON chunk", e);
                }
            }
        }
    }
}

export const streamAnswerFromGemini = async (
    latestMessage: string, 
    history: Message[], 
    orangeBookContext: DrugInfo | null, 
    answerLength: AnswerLength, 
    professionalFocus: ProfessionalFocus,
    llmModel?: LlmModel
): Promise<{ stream: AsyncGenerator<any>; source: MessageSource }> => {
    
    const source: MessageSource = orangeBookContext ? 'local' : 'gemini';
    const shouldAddDrugInstruction = !orangeBookContext;

    // 1. Format History
    const validHistory = history.filter(m => m.text && m.role);
    const historyContents = validHistory.map(msg => ({
        role: msg.role === 'user' ? 'user' : 'model',
        parts: [{ text: msg.text }]
    }));

    let currentPromptText = latestMessage;
    if (orangeBookContext) {
        currentPromptText = `[System Note: User has viewed this drug locally]\nContext: ${JSON.stringify(orangeBookContext)}\n\nUser Query: ${latestMessage}`;
    }

    const contents = [
        ...historyContents,
        { role: 'user', parts: [{ text: currentPromptText }] }
    ];

    const systemInstruction = getSystemInstruction(answerLength, professionalFocus, shouldAddDrugInstruction);

    // 2. Call Backend using relative path
    try {
        const response = await fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents,
                systemInstruction,
                model: llmModel || 'gemini-3.8-flash',
                config: {
                    temperature: answerLength === 'short' ? 0.3 : 0.5 
                }
            })
        });

        if (!response.ok || !response.body) {
            throw new Error(`Backend Error: ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const stream = parseSSEStream(reader);

        return { stream, source };

    } catch (error) {
        console.error("Chat Stream Failed:", error);
        throw error;
    }
};

export const generateChatTitle = async (firstUserMessage: string): Promise<string> => {
    try {
        const res = await getJsonResponse(
            `Generate a title (max 5 words) for this chat: "${firstUserMessage}"`, 
            "Title generator.", 
            null
        );
        return typeof res === 'string' ? res : (res.title || firstUserMessage.substring(0, 30));
    } catch {
        return firstUserMessage.substring(0, 30) + '...';
    }
};

// --- JSON PROXY WITH SESSION CACHING ---
const toolResponseCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL_MS = 1000 * 60 * 60 * 6; // 6-hour cache for deterministic tool queries

const getJsonResponse = async (userPrompt: string, systemInstruction: string, responseSchema: any, model: string = 'gemini-3.8-flash'): Promise<any> => {
    // Generate deterministic cache key to avoid duplicate Cloud Function executions and token costs
    const cacheKey = `${model}_${userPrompt.trim()}_${systemInstruction.substring(0, 40)}`;
    const cached = toolResponseCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
        return cached.data;
    }

    const generateJson = httpsCallable(functions, 'generateJson');
    try {
        const result = await generateJson({
            contents: userPrompt,
            systemInstruction,
            responseSchema,
            model
        });
        const data = result.data as any;
        
        // Cache successful response (prune if exceeds 200 items)
        if (toolResponseCache.size > 200) {
            const firstKey = toolResponseCache.keys().next().value;
            if (firstKey) toolResponseCache.delete(firstKey);
        }
        toolResponseCache.set(cacheKey, { data, timestamp: Date.now() });
        return data;
    } catch (error) {
        console.error(`Gemini JSON Proxy failed:`, error);
        throw error;
    }
};

// --- Tool Functions ---
// (Exported tool functions remain unchanged, just relying on the updated getJsonResponse)

export const performMTMAnalysis = async (patientInfo: any): Promise<MTMResult> => {
    const schema = {
        type: "OBJECT",
        properties: {
            medicationRelatedProblems: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        problem: { type: "STRING" },
                        category: { type: "STRING", enum: ['Untreated Indication', 'Drug Interaction', 'Adverse Effect', 'Suboptimal Dosing', 'Unnecessary Therapy', 'Adherence Concern', 'Safety'] },
                        evidence: { type: "STRING" },
                        recommendation: { type: "STRING" }
                    },
                    required: ['problem', 'category', 'evidence', 'recommendation']
                }
            },
            keyCounselingPoints: { type: "ARRAY", items: { type: "STRING" } },
            monitoringParameters: { type: "ARRAY", items: { type: "STRING" } }
        },
        required: ['medicationRelatedProblems', 'keyCounselingPoints', 'monitoringParameters']
    };
    const systemInstruction = "You are an expert clinical pharmacist. Identify MRPs.";
    return await getJsonResponse(`Analyze: ${JSON.stringify(patientInfo)}`, systemInstruction, schema, 'gemini-3.8-flash');
};

export const generatePatientMAP = async (problems: any[]): Promise<string> => {
    const schema = { type: "OBJECT", properties: { text: { type: "STRING" } } };
    const res: any = await getJsonResponse(`Create Patient Plan for: ${JSON.stringify(problems)}`, "Patient-friendly language.", schema);
    return res.text;
};

export const generateSOAPNote = async (problems: any[], patientInfo: any): Promise<string> => {
    const schema = { type: "OBJECT", properties: { text: { type: "STRING" } } };
    const res: any = await getJsonResponse(`SOAP Note for: ${JSON.stringify(patientInfo)} Issues: ${JSON.stringify(problems)}`, "Professional SOAP format.", schema);
    return res.text;
};

export const getPregnancyInfo = async (drugName: string): Promise<PregnancyLactationResult> => {
    const schema = {
        type: "OBJECT",
        properties: {
            riskCategory: { type: "STRING", enum: ['Contraindicated', 'Use with Caution', 'Compatible', 'Unknown'] },
            pregnancySummary: { type: "STRING" },
            lactationSummary: { type: "STRING" },
            reproductivePotentialSummary: { type: "STRING" }
        },
        required: ['riskCategory', 'pregnancySummary', 'lactationSummary', 'reproductivePotentialSummary']
    };
    return await getJsonResponse(`Pregnancy/Lactation info for ${drugName}`, "Teratology expert.", schema);
};

export const getDrugInfoFromGeminiForModal = async (drugName: string): Promise<Partial<DrugInfo>> => {
    const schema = {
        type: "OBJECT",
        properties: {
            brandName: { type: "STRING" },
            genericName: { type: "STRING" },
            therapeuticClass: { type: "STRING" },
            dosageForm: { type: "STRING" },
            strength: { type: "STRING" },
            manufacturer: { type: "STRING" },
            therapeuticEquivalence: { type: "STRING" },
            isRx: { type: "BOOLEAN" },
            isOtc: { type: "BOOLEAN" },
            hasBlackBoxWarning: { type: "BOOLEAN" },
            blackBoxWarning: { type: "STRING", nullable: true },
            contraindications: { type: "STRING" },
            drugInteractions: { type: "STRING" },
            pharmacology: {
                type: "OBJECT",
                properties: {
                    mechanismOfAction: { type: "STRING" },
                    pharmacokinetics: {
                        type: "OBJECT",
                        properties: {
                            absorption: { type: "STRING" },
                            distribution: { type: "STRING" },
                            metabolism: { type: "STRING" },
                            excretion: { type: "STRING" },
                        }
                    }
                }
            }
        }
    };
    return await getJsonResponse(`Extract info for: ${drugName}`, "Drug info extractor.", schema);
};

export const generateClinicalPearl = async (): Promise<string> => {
    const schema = { type: "OBJECT", properties: { text: { type: "STRING" } } };
    const res: any = await getJsonResponse("Generate one concise clinical pearl.", "Pharmacy educator.", schema);
    return res.text;
};

export const generateAlerts = async (type: string, context?: any): Promise<Alert[]> => {
    const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                id: { type: "STRING" },
                source: { type: "STRING" },
                severity: { type: "STRING", enum: ['High', 'Medium', 'Low'] },
                title: { type: "STRING" },
                summary: { type: "STRING" },
                date: { type: "STRING" },
                link: { type: "STRING" },
                details: { type: "STRING" },
            },
            required: ['id', 'source', 'title', 'summary', 'date', 'details']
        }
    };
    return await getJsonResponse(`Generate ${type} alerts.`, "Generate realistic alerts.", schema);
};

export const generateDetailedRecalls = async (timeFrame: string): Promise<FdaRecall[]> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                id: { type: "STRING" },
                recallClass: { type: "STRING", enum: ['Class I', 'Class II', 'Class III'] },
                drugName: { type: "STRING" },
                manufacturer: { type: "STRING" },
                recallDate: { type: "STRING" },
                reason: { type: "STRING" },
                ndc: { type: "STRING" },
            }
        }
    };
    return await getJsonResponse(`Generate FDA recalls: ${timeFrame}`, "FDA recalls generator.", schema);
};

export const generateCdcAdvisories = async (timeFrame: string): Promise<CdcAdvisory[]> => {
    const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                id: { type: "STRING" },
                title: { type: "STRING" },
                category: { type: "STRING" },
                date: { type: "STRING" },
                summary: { type: "STRING" },
                link: { type: "STRING" },
            }
        }
    };
    return await getJsonResponse(`Generate CDC advisories: ${timeFrame}`, "CDC advisories generator.", schema);
};

export const generateDeaUpdates = async (timeFrame: string): Promise<DeaUpdate[]> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                id: { type: "STRING" },
                title: { type: "STRING" },
                category: { type: "STRING" },
                date: { type: "STRING" },
                summary: { type: "STRING" },
                link: { type: "STRING" },
            }
        }
    };
    return await getJsonResponse(`Generate DEA updates: ${timeFrame}`, "DEA updates generator.", schema);
};

export const findEvidence = async (query: string, sources: string[], focus: string): Promise<EvidenceSearchResult> => {
     const schema = {
        type: "OBJECT",
        properties: {
            synthesis: { type: "STRING" },
            articles: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        id: { type: "STRING" },
                        source: { type: "STRING" },
                        title: { type: "STRING" },
                        authors: { type: "STRING" },
                        publicationDate: { type: "STRING" },
                        summary: { type: "STRING" },
                        link: { type: "STRING" },
                    }
                }
            }
        }
    };
    return await getJsonResponse(`Find evidence: ${query} from ${sources.join(', ')}`, `Research assistant for ${focus}.`, schema, 'gemini-3.8-flash');
};

export const calculateMME = async (meds: any[]): Promise<MMEResult> => {
     const schema = {
        type: "OBJECT",
        properties: {
            totalDailyMme: { type: "NUMBER" },
            riskAssessment: { type: "STRING" },
            breakdown: {
                type: "ARRAY",
                items: { type: "OBJECT", properties: { drug: { type: "STRING" }, mme: { type: "NUMBER" } } }
            }
        }
    };
    return await getJsonResponse(`Calculate MME: ${JSON.stringify(meds)}`, "MME Calculator.", schema);
};

export const getDoseAdjustment = async (drug: string, type: string, value: string): Promise<DoseAdjustmentResult> => {
    const schema = {
        type: "OBJECT",
        properties: { recommendation: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Adjust ${drug} for ${type} ${value}`, "Dose adjustment expert.", schema);
};

export const checkIVCompatibility = async (drugs: string, type: string): Promise<IVCompatibilityResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            status: { type: "STRING", enum: ['Compatible', 'Incompatible', 'Variable', 'Uncertain'] }, 
            notes: { type: "STRING" } 
        }
    };
    return await getJsonResponse(`IV compatibility (${type}): ${drugs}`, "Trissel's data expert.", schema);
};

export const checkDrugInteractions = async (drugs: string[]): Promise<DrugInteractionResult> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                drugs: { type: "ARRAY", items: { type: "STRING" } },
                severity: { type: "STRING" },
                mechanism: { type: "STRING" },
                management: { type: "STRING" },
            }
        }
    };
    return await getJsonResponse(`Check interactions: ${drugs.join(', ')}`, "Interaction checker.", schema);
};

export const generateCounselingPoints = async (drug: string, literacy: string): Promise<CounselingPointsResult> => {
    const schema = { type: "OBJECT", properties: { counselingPoints: { type: "STRING" } } };
    return await getJsonResponse(`Counseling for ${drug}, literacy: ${literacy}`, "Patient counselor.", schema);
};

export const identifyPill = async (imprint: string, shape: string, color: string): Promise<PillIdentifierResult> => {
    const schema = {
        type: "OBJECT",
        properties: {
            matches: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        drugName: { type: "STRING" },
                        details: { type: "STRING" },
                        confidence: { type: "STRING", enum: ['High', 'Medium', 'Low'] }
                    }
                }
            }
        }
    };
    return await getJsonResponse(`Identify pill: ${imprint}, ${shape}, ${color}`, "Pill identifier.", schema);
};

export const getAnticoagBridging = async (drug: string, procedure: string, thromboticRisk: string, bleedingRisk: string): Promise<AnticoagBridgingResult> => {
    const schema = {
        type: "OBJECT",
        properties: { preProcedurePlan: { type: "STRING" }, postProcedurePlan: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Bridging: ${drug}, ${procedure}, Thrombotic: ${thromboticRisk}, Bleed: ${bleedingRisk}`, "Anticoag expert.", schema);
};

export const getTherapeuticInterchange = async (from: string, to: string): Promise<TherapeuticInterchangeResult> => {
     const schema = {
        type: "OBJECT",
        properties: { recommendation: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Interchange ${from} to ${to}`, "Therapeutic interchange.", schema);
};

export const getDaysSupply = async (drug: string, qty: string, sig: string): Promise<DaysSupplyResult> => {
     const schema = {
        type: "OBJECT",
        properties: { daysSupply: { type: "NUMBER" }, rationale: { type: "STRING" } }
    };
    return await getJsonResponse(`Days supply: ${drug}, Qty ${qty}, Sig ${sig}`, "Pharmacy math.", schema);
};

export const getCompoundingCalc = async (c1: string, c2: string, v2: string): Promise<CompoundingCalcResult> => {
     const schema = {
        type: "OBJECT",
        properties: { formula: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Dilution: C1 ${c1}, C2 ${c2}, V2 ${v2}`, "Compounding calc.", schema);
};

export const getStateLawInfo = async (state: string, topic: string): Promise<LawResult> => {
    const schema = {
        type: "OBJECT",
        properties: { summary: { type: "STRING" }, reference: { type: "STRING" } }
    };
    return await getJsonResponse(`Law info ${state}: ${topic}`, "Law expert.", schema);
};

export const getFederalLawInfo = async (topic: string): Promise<LawResult> => {
     const schema = {
        type: "OBJECT",
        properties: { summary: { type: "STRING" }, reference: { type: "STRING" } }
    };
    return await getJsonResponse(`Federal law: ${topic}`, "Federal law expert.", schema);
};

export const getAntibioticInfo = async (query: string): Promise<AntibioticInfoResult> => {
     const schema = {
        type: "OBJECT",
        properties: { spectrum: { type: "STRING" }, indications: { type: "STRING" }, dosing: { type: "STRING" }, adjustments: { type: "STRING" } }
    };
    return await getJsonResponse(`Antibiotic info: ${query}`, "ID expert.", schema);
};

export const getGuidelineSummary = async (topic: string, org: string): Promise<GuidelineSummaryResult> => {
     const schema = {
        type: "OBJECT",
        properties: { summary: { type: "STRING" }, keyRecommendations: { type: "STRING" }, sourceReference: { type: "STRING" } }
    };
    return await getJsonResponse(`Summarize ${org} guidelines: ${topic}`, "Guideline summarizer.", schema);
};

export const getTaperingSchedule = async (drug: string, context: string): Promise<DrugTaperingResult> => {
     const schema = {
        type: "OBJECT",
        properties: { schedule: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Taper ${drug}. Context: ${context}`, "Tapering expert.", schema);
};

export const getPediatricDose = async (drug: string, indication: string, weight: string): Promise<PediatricDoseResult> => {
     const schema = {
        type: "OBJECT",
        properties: { calculatedDose: { type: "STRING" }, dosePerKg: { type: "STRING" }, frequency: { type: "STRING" }, maxDose: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Peds dose ${drug}, ${indication}, ${weight}`, "Pediatric expert.", schema);
};

export const calculateCrCl = async (age: string, weight: string, scr: string, gender: string): Promise<PharmacokineticsResult> => {
     const schema = {
        type: "OBJECT",
        properties: { creatinineClearance: { type: "NUMBER" }, interpretation: { type: "STRING" }, formulaUsed: { type: "STRING" } }
    };
    return await getJsonResponse(`CrCl: ${age}yo ${gender}, ${weight}, SCr ${scr}`, "PK calculator.", schema);
};

export const getAnticholinergicBurden = async (drugs: string): Promise<AnticholinergicBurdenResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            totalScore: { type: "NUMBER" }, 
            riskLevel: { type: "STRING", enum: ['Low', 'Moderate', 'High', 'Very High'] }, 
            breakdown: { type: "ARRAY", items: { type: "OBJECT", properties: { drugName: { type: "STRING" }, score: { type: "NUMBER" }, rationale: { type: "STRING" } } } } 
        }
    };
    return await getJsonResponse(`ACB score: ${drugs}`, "ACB calculator.", schema);
};

export const getInsulinConversion = async (from: string, to: string): Promise<InsulinConversionResult> => {
     const schema = {
        type: "OBJECT",
        properties: { recommendedDose: { type: "STRING" }, clinicalNotes: { type: "STRING" } }
    };
    return await getJsonResponse(`Convert insulin ${from} to ${to}`, "Insulin expert.", schema);
};

export const getMissedDoseInfo = async (drug: string): Promise<MissedDoseResult> => {
     const schema = {
        type: "OBJECT",
        properties: { guidance: { type: "STRING" } }
    };
    return await getJsonResponse(`Missed dose: ${drug}`, "Safety expert.", schema);
};

export const getVaccineRecommendations = async (age: string, conditions: string[]): Promise<VaccineRecResult> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                vaccineName: { type: "STRING" },
                rationale: { type: "STRING" },
                category: { type: "STRING", enum: ['Routinely Recommended', 'Recommended Based on Medical Conditions', 'Shared Clinical Decision-Making'] }
            }
        }
    };
    return await getJsonResponse(`Vaccines for ${age}yo, conds: ${conditions.join(', ')}`, "ACIP expert.", schema);
};

export const checkBeersCriteria = async (query: string): Promise<BeersCriteriaResult> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                drugName: { type: "STRING" },
                reason: { type: "STRING" },
                alternatives: { type: "STRING" },
            }
        }
    };
    return await getJsonResponse(`Beers check: ${query}`, "Geriatric expert.", schema);
};

export const getAntidoteInfo = async (toxin: string): Promise<AntidoteResult> => {
     const schema = {
        type: "OBJECT",
        properties: { antidote: { type: "STRING" }, dosing: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Antidote for ${toxin}`, "Tox expert.", schema);
};

export const getDrugShortageInfo = async (drug: string): Promise<DrugShortageResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            status: { type: "STRING", enum: ['On Shortage', 'Limited Supply', 'Available', 'Unknown'] },
            lastUpdated: { type: "STRING" },
            alternatives: { type: "STRING" }
        }
    };
    return await getJsonResponse(`Shortage status: ${drug}`, "Shortage tracker.", schema);
};

export const getLabValueInfo = async (test: string): Promise<LabValueResult> => {
     const schema = {
        type: "OBJECT",
        properties: { testName: { type: "STRING" }, normalRange: { type: "STRING" }, interpretationHigh: { type: "STRING" }, interpretationLow: { type: "STRING" } }
    };
    return await getJsonResponse(`Lab info: ${test}`, "Lab expert.", schema);
};

export const getDoseConversion = async (from: string, to: string): Promise<DoseConversionResult> => {
     const schema = {
        type: "OBJECT",
        properties: { equivalentDose: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Convert dose ${from} to ${to}`, "Conversion expert.", schema);
};

export const findOTCProduct = async (query: string): Promise<OTCResult> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: { productName: { type: "STRING" }, use: { type: "STRING" }, counselingPoints: { type: "STRING" } }
        }
    };
    return await getJsonResponse(`Recommend OTC: ${query}`, "OTC expert.", schema);
};

export const getCSInfo = async (drug: string, state: string): Promise<CSReferenceResult> => {
     const schema = {
        type: "OBJECT",
        properties: { schedule: { type: "STRING" }, federalSummary: { type: "STRING" }, stateSummary: { type: "STRING" } }
    };
    return await getJsonResponse(`CS info ${drug} in ${state}`, "Law expert.", schema);
};

export const getDrugPriceInfo = async (drug: string, qty: string, zip: string): Promise<DrugPriceResult> => {
     const schema = {
        type: "OBJECT",
        properties: { estimatedPriceRange: { type: "STRING" }, notes: { type: "STRING" } }
    };
    return await getJsonResponse(`Price for ${drug} ${qty} in ${zip}`, "Price estimator.", schema);
};

export const generatePAJustification = async (drug: string, indication: string, justification: string): Promise<PAJustificationResult> => {
     const schema = {
        type: "OBJECT",
        properties: { justificationText: { type: "STRING" } }
    };
    return await getJsonResponse(`PA letter ${drug}, ${indication}. Context: ${justification}`, "PA expert.", schema);
};

export const translateSigCode = async (input: string): Promise<SigCodeResult> => {
     const schema = {
        type: "OBJECT",
        properties: { translation: { type: "STRING" }, isSigToEnglish: { type: "BOOLEAN" } }
    };
    return await getJsonResponse(`Translate sig: ${input}`, "Sig translator.", schema);
};

export const getEmergencyGuideInfo = async (query: string): Promise<EmergencyGuideResult> => {
     const schema = {
        type: "OBJECT",
        properties: { immediateActions: { type: "STRING" }, drugTherapy: { type: "STRING" }, monitoring: { type: "STRING" } }
    };
    return await getJsonResponse(`Emergency info: ${query}`, "ER expert.", schema);
};

export const analyzePrescription = async (data: any): Promise<AnalyzeRxResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            sigTranslation: { type: "STRING" }, 
            daysSupply: { type: "STRING" }, 
            potentialIssues: { type: "ARRAY", items: { type: "OBJECT", properties: { type: { type: "STRING", enum: ['Clinical', 'Legal', 'Clarity'] }, description: { type: "STRING" } } } } 
        }
    };
    return await getJsonResponse(`Analyze Rx: ${JSON.stringify(data)}`, "Safety analyst.", schema);
};

export const findClinicalTrials = async (query: string): Promise<ClinicalTrialResult> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: { nctNumber: { type: "STRING" }, title: { type: "STRING" }, status: { type: "STRING" }, phase: { type: "STRING" }, summary: { type: "STRING" }, link: { type: "STRING" } }
        }
    };
    return await getJsonResponse(`Clinical trials: ${query}`, "Trials finder.", schema);
};

export const getDietaryInfo = async (drug: string): Promise<DietaryGuideResult> => {
     const schema = {
        type: "OBJECT",
        properties: { withFood: { type: "STRING" }, foodsToAvoid: { type: "STRING" }, generalAdvice: { type: "STRING" } }
    };
    return await getJsonResponse(`Dietary info: ${drug}`, "Nutrition expert.", schema);
};

export const analyzeFormulary = async (drug: string, context: string): Promise<FormularyResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            status: { type: "STRING", enum: ['Covered', 'Not Covered', 'Prior Auth Required', 'Non-Preferred', 'Unknown'] }, 
            tier: { type: "STRING" }, 
            alternatives: { type: "STRING" } 
        }
    };
    return await getJsonResponse(`Formulary check: ${drug}. Context: ${context}`, "Formulary expert.", schema);
};

export const getHivInfo = async (query: string): Promise<HivInfoResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            summary: { type: "STRING" }, 
            details: { type: "OBJECT", properties: { class: { type: "STRING" }, dosing: { type: "STRING" }, sideEffects: { type: "STRING" }, interactions: { type: "STRING" } } } 
        }
    };
    return await getJsonResponse(`HIV info: ${query}`, "HIV expert.", schema);
};

export const getInrAdjustment = async (inr: string, target: string, context: string): Promise<InrResult> => {
     const schema = {
        type: "OBJECT",
        properties: { recommendation: { type: "STRING" }, followUp: { type: "STRING" } }
    };
    return await getJsonResponse(`INR adjust: ${inr}, Target ${target}, Context ${context}`, "Anticoag expert.", schema);
};

export const getPgxInfo = async (drug: string, gene: string): Promise<PgxResult> => {
     const schema = {
        type: "OBJECT",
        properties: { implication: { type: "STRING" }, recommendation: { type: "STRING" } }
    };
    return await getJsonResponse(`PGx: ${drug} ${gene}`, "PGx expert.", schema);
};

export const getBiostatsInfo = async (query: string): Promise<BiostatsResult> => {
     const schema = {
        type: "OBJECT",
        properties: { term: { type: "STRING" }, definition: { type: "STRING" }, example: { type: "STRING" } }
    };
    return await getJsonResponse(`Biostats: ${query}`, "Stats tutor.", schema);
};

export const getGenericCalculation = async (query: string): Promise<GenericCalcResult> => {
     const schema = {
        type: "OBJECT",
        properties: { calculationName: { type: "STRING" }, formula: { type: "STRING" }, steps: { type: "STRING" }, result: { type: "STRING" } }
    };
    return await getJsonResponse(`Calculate: ${query}`, "Clinical calculator.", schema);
};

export const getCannabinoidInfo = async (query: string): Promise<CannabinoidInfoResult> => {
     const schema = {
        type: "OBJECT",
        properties: { name: { type: "STRING" }, mechanism: { type: "STRING" }, uses: { type: "STRING" }, adverseEffects: { type: "STRING" }, interactions: { type: "STRING" }, legalNote: { type: "STRING" } }
    };
    return await getJsonResponse(`Cannabinoid info: ${query}`, "Cannabis expert.", schema);
};

export const getChemoToxicity = async (drug: string): Promise<ChemoToxicityResult> => {
     const schema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: { toxicity: { type: "STRING" }, system: { type: "STRING", enum: ['Kidney', 'Ear', 'Heart', 'Lung', 'Nerves', 'Bone Marrow', 'Mouth', 'General'] } }
        }
    };
    return await getJsonResponse(`Chemo tox: ${drug}`, "Onc expert.", schema);
};

export const getCompoundingFormula = async (query: string): Promise<CompoundingFormulaResult> => {
     const schema = {
        type: "OBJECT",
        properties: { 
            name: { type: "STRING" }, 
            disclaimer: { type: "STRING" }, 
            ingredients: { type: "ARRAY", items: { type: "OBJECT", properties: { name: { type: "STRING" }, quantity: { type: "STRING" } } } }, 
            instructions: { type: "STRING" } 
        }
    };
    return await getJsonResponse(`Compounding recipe: ${query}`, "Compounding expert.", schema);
};

export const getMnemonic = async (query: string): Promise<MnemonicResult> => {
     const schema = {
        type: "OBJECT",
        properties: { topic: { type: "STRING" }, mnemonic: { type: "STRING" }, explanation: { type: "STRING" } }
    };
    return await getJsonResponse(`Mnemonic for: ${query}`, "Medical mnemonics.", schema);
};

export const getVitaminInfo = async (query: string): Promise<VitaminResult> => {
     const schema = {
        type: "OBJECT",
        properties: { name: { type: "STRING" }, functions: { type: "STRING" }, deficiencySigns: { type: "STRING" }, sources: { type: "STRING" } }
    };
    return await getJsonResponse(`Vitamin info: ${query}`, "Nutrition expert.", schema);
};